import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { EmployeeRole, UserContextUserFragment } from '@/types';
import { IntercomIdentity, IntercomUserCustomAttributes } from './types';
import { partnerSupplier } from '@/utils/environment';
import { Platform } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useMemo } from 'react';

export const getOperatorIntercomCustomAttributes = (
  selectedRestaurantRelationship: EmployeeRestaurant,
  supplierEmployeeRole: EmployeeRole | null,
  user: UserContextUserFragment,
): IntercomUserCustomAttributes => {
  const attributes: IntercomUserCustomAttributes = {
    pepper_business_id: selectedRestaurantRelationship.uuid,
    pepper_business_organization_id: partnerSupplier.businessOrganizationUUID,
    pepper_is_supplier_employee: Boolean(supplierEmployeeRole),
  };

  const loginEmail = user.contact_info.login_email;
  if (loginEmail) {
    attributes.pepper_employee_login_email = loginEmail;
  }

  const employeeRole =
    supplierEmployeeRole ?? selectedRestaurantRelationship.employeeRole;
  if (employeeRole) {
    attributes.pepper_employee_role = employeeRole;
  }

  return attributes;
};

/**
 * Operator's Intercom identity hook. Preserves today's per-platform gate
 * exactly: web only boots for supplier employees, native boots for all
 * restaurant employees.
 *
 * Accepts (and ignores) the same params shape as other experiences' identity
 * hooks, since it pulls everything from UserContext instead.
 */
export const useOperatorIntercomIdentity = (): IntercomIdentity | null => {
  const intercomEnabled = useDynamicConfigValue('intercom_enabled');
  const { selectedRestaurantRelationship, supplierEmployeeRole, user } =
    useAuthenticatedUserContext();

  return useMemo<IntercomIdentity | null>(() => {
    const gateBlocked = Platform.OS === 'web' && !supplierEmployeeRole;
    if (!intercomEnabled || gateBlocked) {
      return null;
    }

    return {
      companyId: supplierEmployeeRole
        ? selectedRestaurantRelationship.supplierUUID
        : undefined,
      customAttributes: getOperatorIntercomCustomAttributes(
        selectedRestaurantRelationship,
        supplierEmployeeRole,
        user,
      ),
      email: user.contact_info.email ?? undefined,
      userId: user.uuid,
    };
  }, [
    intercomEnabled,
    selectedRestaurantRelationship,
    supplierEmployeeRole,
    user,
  ]);
};
